import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";

import { ActionButton, FormDialog, InputField, PhoneInput } from "../v3";
import { PurchaseService } from "../../services";
import { subcontractorValidationRules } from "../../pages/subcontractors/validation";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const AddEditSubContractor = ({ open, onClose, contractorId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [subcontractorDetails, setSubcontractorDetails] = useState({
    name: "",
    primaryPhone: undefined,
    primaryAddress: {
      locality: "",
    },
  });

  const isValid = isPhoneValid(subcontractorDetails.primaryPhone);
  const showError = touched && !isValid;

  useEffect(() => {
    if (open && contractorId) {
      fetchData(contractorId);
    } else if (open && !contractorId) {
      handleClearForm();
    }
  }, [open, contractorId]);

  const fetchData = async (id) => {
    try {
      const response = await PurchaseService.getVendorById(id);
      setSubcontractorDetails({
        name: response?.data?.name || "",
        primaryPhone: response.data.primaryPhone || undefined,
        primaryAddress: {
          locality: response?.data?.primaryAddress?.locality || "",
        },
      });
    } catch (error) {
      toast.error("Failed to fetch contractor details");
      console.error("Error fetching vendor data:", error);
    }
  };

  const handleClearForm = () => {
    setSubcontractorDetails({
      name: "",
      primaryPhone: undefined,
      primaryAddress: {
        locality: "",
      },
    });
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    if (["locality"].includes(name)) {
      setSubcontractorDetails((prev) => ({
        ...prev,
        primaryAddress: {
          ...prev.primaryAddress,
          [name]: value,
        },
      }));
    } else {
      setSubcontractorDetails((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const handleSaveSubcontractor = async () => {
    setErrors({});
    const errors = subcontractorValidationRules(subcontractorDetails);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    subcontractorDetails.primaryAddress = {
      ...subcontractorDetails.primaryAddress,
      country: "",
      state: "",
      pinCode: "",
    };

    setLoading(true);
    try {
      if (contractorId) {
        await PurchaseService.updateVendor(contractorId, subcontractorDetails);
        toast.success("Subcontractor updated successfully");
      } else {
        await PurchaseService.createVendor({
          ...subcontractorDetails,
          type: "SUBCONTRACTOR",
        });
        toast.success("Subcontractor added successfully");
      }

      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${contractorId ? "updating" : "adding"} contractor`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.log("🚀 ~ handleSaveSubcontractor ~ error:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <FormDialog
      open={open}
      title={contractorId ? "Edit Subcontractor" : "Add Subcontractor"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveSubcontractor} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Name"
            name="name"
            value={subcontractorDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <InputField
            label="place"
            name="locality"
            value={subcontractorDetails.primaryAddress.locality}
            onChange={updateDetailsState}
            fullWidth
            autoComplete="on"
          />
        </Box>
        <Box display="flex" gap={2}>
          <PhoneInput
            label="Mobile Number"
            defaultCountry="in"
            value={subcontractorDetails.primaryPhone}
            size="small"
            fullWidth
            onChange={(e164) => setSubcontractorDetails((prev) => ({ ...prev, primaryPhone: e164 }))}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? "Enter a valid phone number" : " "}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
